import { PaginatedResponse } from "@elit-hair/elit-tools";

import { elitaiSvcAxiosInstance } from "@/constants";
import { darwinEndpoints } from "@/constants/endpoints";
import { ChatMessageRating, DarwinChatRequest, StreamEventData } from "@/types/chatbot.types";
import { DarwinConversation, DarwinConversationFilters, DarwinTranscript } from "@/types/darwin-conversation.types";
import { DarwinCost } from "@/types/darwin-cost.types";
import { normalizeError } from "@/utils/error.utils";

import { makeAuthenticatedStreamRequest } from "./httpUtils";

type DarwinStreamCallbacks = {
    onEvent: (event: StreamEventData) => void;
    onComplete: () => void;
    onError: (error: Error) => void;
};

const SSE_EVENT_SEPARATOR = "\n\n";
const SSE_DATA_PREFIX = "data: ";

function parseSseEvent(rawEvent: string): StreamEventData | null {
    const dataLine = rawEvent.split("\n").find((line) => line.startsWith(SSE_DATA_PREFIX));
    if (!dataLine) return null;

    try {
        return JSON.parse(dataLine.slice(SSE_DATA_PREFIX.length)) as StreamEventData;
    } catch {
        console.warn("Failed to parse Darwin stream event");
        return null;
    }
}

/**
 * Darwin AI agent (elitai-svc). Streams the reply to one user turn as server-sent events.
 */
class DarwinService {
    async streamReply(
        request: DarwinChatRequest & { auth: unknown; abortController: AbortController },
        { onEvent, onComplete, onError }: DarwinStreamCallbacks
    ): Promise<void> {
        const { auth, abortController, ...payload } = request;

        try {
            const response = await makeAuthenticatedStreamRequest(
                darwinEndpoints.chatStream,
                payload,
                auth,
                abortController
            );
            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error("No response body reader available");
            }

            const decoder = new TextDecoder();
            let buffer = "";

            for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) {
                // Events can be split across network chunks, so only parse complete ones
                buffer += decoder.decode(chunk.value, { stream: true });
                const rawEvents = buffer.split(SSE_EVENT_SEPARATOR);
                buffer = rawEvents.pop() ?? "";

                for (const rawEvent of rawEvents) {
                    const event = parseSseEvent(rawEvent);
                    if (!event) continue;
                    if (event.type === "error") {
                        throw new Error(event.data);
                    }
                    onEvent(event);
                }
            }

            onComplete();
        } catch (error) {
            if (abortController.signal.aborted) return;
            onError(normalizeError(error));
        }
    }

    /**
     * Darwin's chat route is on elitai-svc. Health can be green while this route is missing,
     * which is what an old container returns.
     */
    async checkConnection(): Promise<boolean> {
        const response = await elitaiSvcAxiosInstance.post(
            "/darwin/chat/stream",
            {},
            {
                timeout: 8000,
                validateStatus: () => true,
            }
        );
        if (response.status === 404) {
            throw new Error("Darwin is not available on elitai-svc");
        }
        return true;
    }

    /** Saves the rating of one Darwin reply. */
    async rateMessage({
        conversationId,
        messageId,
        rating,
    }: {
        conversationId: string;
        messageId: string;
        rating?: ChatMessageRating;
    }): Promise<void> {
        await elitaiSvcAxiosInstance.patch(darwinEndpoints.message({ conversationId, messageId }), {
            rating: rating ?? null,
        });
    }

    async getConversations(filters: DarwinConversationFilters): Promise<PaginatedResponse<DarwinConversation>> {
        const response = await elitaiSvcAxiosInstance.get<PaginatedResponse<DarwinConversation>>(
            darwinEndpoints.conversations,
            { params: filters }
        );
        return response.data;
    }

    async getTranscript(conversationId: string): Promise<DarwinTranscript> {
        const response = await elitaiSvcAxiosInstance.get<DarwinTranscript>(darwinEndpoints.transcript(conversationId));
        return response.data;
    }

    async getCost(userId?: string): Promise<DarwinCost> {
        const response = await elitaiSvcAxiosInstance.get<DarwinCost>(darwinEndpoints.cost, { params: { userId } });
        return response.data;
    }

    /** Message ids in this conversation that already filed a tool request. */
    async getRequestedToolMessageIds(conversationId: string): Promise<string[]> {
        const response = await elitaiSvcAxiosInstance.get<{ requestedToolMessageIds: string[] }>(
            darwinEndpoints.conversation(conversationId)
        );
        return response.data.requestedToolMessageIds;
    }

    /** Asks the support team to add a capability Darwin does not have. Returns the incident that was filed. */
    async requestTool({
        conversationId,
        messageId,
    }: {
        conversationId: string;
        messageId: string;
    }): Promise<{ incidentId: string }> {
        const response = await elitaiSvcAxiosInstance.post<{ incidentId: string }>(
            darwinEndpoints.toolRequest({ conversationId, messageId })
        );
        return response.data;
    }
}

export const darwinService = new DarwinService();
